import { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { db } from "./firebase";
import { collection, addDoc, Timestamp, doc, setDoc, deleteDoc } from "firebase/firestore";
import AOS from "aos";
import "aos/dist/aos.css";
import { 
  FaWhatsapp, FaArrowRight, FaCheck, FaUserMd, FaClock, FaFileMedical, 
  FaPrescriptionBottleAlt, FaFlask, FaFileAlt, FaHeadset, FaShieldAlt,
  FaStar, FaVideo, FaCalendarCheck, FaAward, FaStethoscope,
  FaHeartbeat, FaUserShield, FaCertificate, FaRocket, FaPhoneAlt,
  FaHeart, FaSyringe, FaWeight, FaAppleAlt
} from "react-icons/fa";

import logo from "./IMG/logo.png";
import logo2 from "./IMG/logo2.png";
import doutoraFoto from "./IMG/foto.jpeg";

import "./App.css";

// Componente principal que será exibido em todas as rotas
function MainContent() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeUsers, setActiveUsers] = useState(0);
  const featuresRef = useRef(null);
  const ctaRef = useRef(null);
  const heroRef = useRef(null);

  // Form state (mantendo original: nome, data nascimento, email)
  const [formData, setFormData] = useState({
    name: '',
    birthdate: '',
    email: ''
  });

  useEffect(() => {
    AOS.init({ 
      duration: 1000,
      once: true,
      easing: 'ease-in-out-quart'
    });

    // Registrar visita (mantendo nomenclatura original)
    recordDoutorAgoraVisit();
    registerDoutorAgoraActiveUser();

    // Simular usuários online
    const updateActiveUsers = () => {
      setActiveUsers(Math.floor(Math.random() * 15) + 8);
    };
    updateActiveUsers();
    const usersInterval = setInterval(updateActiveUsers, 30000);

    // Observador de interseção para animações
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (featuresRef.current) observer.observe(featuresRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);
    if (heroRef.current) observer.observe(heroRef.current);

    return () => {
      clearInterval(usersInterval);
      if (featuresRef.current) observer.unobserve(featuresRef.current);
      if (ctaRef.current) observer.unobserve(ctaRef.current);
      if (heroRef.current) observer.unobserve(heroRef.current);
    };
  }, []);

  // Função para registrar conversão no Google Ads (mantendo original)
  const trackConversion = (url, buttonName) => {
    // Primeiro registra o clique no Firestore
    recordDoutorAgoraClick(buttonName);
    
    // Depois dispara a conversão do Google Ads
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17706776522/Y5UaCImQ5cEbEMrvn_tB',
      'value': 99.99,
      'currency': 'BRL',
      'event_callback': () => {
        window.open(url, '_blank');
      }
    });
    return false;
  };

  // Função para registrar uma visita no Firestore (mantendo nomenclatura)
  const recordDoutorAgoraVisit = async () => {
    try {
      await addDoc(collection(db, "doutorAgoraPageVisits"), {
        timestamp: Timestamp.now(),
      });
    } catch (error) {
      console.error("Erro ao registrar visita:", error);
    }
  };

  // Função para registrar um usuário ativo (mantendo nomenclatura)
  const registerDoutorAgoraActiveUser = async () => {
    const userRef = doc(collection(db, "doutorAgoraActiveUsers"), `user_${Date.now()}`);
    await setDoc(userRef, {
      lastActive: Timestamp.now(),
    });

    const heartbeatInterval = setInterval(async () => {
      await setDoc(userRef, { lastActive: Timestamp.now() }, { merge: true });
    }, 5000);

    window.addEventListener("beforeunload", async () => {
      clearInterval(heartbeatInterval);
      await deleteDoc(userRef);
    });
  };

  // Função para registrar um clique no botão (mantendo nomenclatura)
  const recordDoutorAgoraClick = async (buttonName) => {
    try {
      await addDoc(collection(db, "doutorAgoraClickMetrics"), {
        button: buttonName,
        timestamp: Timestamp.now(),
      });
    } catch (error) {
      console.error("Erro ao registrar clique:", error);
    }
  };

  // Form handlers (mantendo original)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `🏥 AGENDAMENTO - Endocrinologista Agora\n\n👤 Nome: ${formData.name}\n🎂 Data Nascimento: ${formData.birthdate}\n📧 E-mail: ${formData.email}\n\n`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/5538999267532?text=${encodedMessage}`;
    
    trackConversion(whatsappUrl, "FORMULARIO DE AGENDAMENTO");
    
    setFormData({
      name: '',
      birthdate: '',
      email: ''
    });
  };

  return (
    <div className="doutor-agora-container">
      {/* Barra de Urgência Premium */}
      <div className="urgency-bar-premium">
        <div className="container">
          <div className="urgency-content-premium">
            <div className="urgency-indicator-premium">
              <div className="pulse-dot-premium"></div>
              <span><strong>{activeUsers} pessoas</strong> consultando endocrinologistas agora</span>
            </div>
            <div className="urgency-cta-premium">
              <button 
                className="mini-cta-button-premium"
                onClick={() => trackConversion("https://wa.me/5538999267532", "BARRA URGENCIA PREMIUM")}
              >
                <FaWhatsapp /> ATENDIMENTO ESPECIALIZADO
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section Premium */}
      <header className="hero-section-premium" ref={heroRef}>
        <div className="hero-effects-premium">
          <div className="medical-crosses-premium">
            <div className="cross-1"></div>
            <div className="cross-2"></div>
            <div className="cross-3"></div>
          </div>
          <div className="light-effect-premium"></div>
        </div>

        <div className="container">
          <div className="hero-header-premium" data-aos="fade-down">
            <div className="logo-container-premium">
              <img src={logo} alt="Endocrinologista Agora" className="logo-premium" />
            </div>
            <div className="trust-badges-premium">
              <div className="trust-badge-premium">
                <FaCertificate />
                <span>CFM Aprovado</span>
              </div>
              <div className="trust-badge-premium">
                <FaShieldAlt />
                <span>100% Seguro</span>
              </div>
              <div className="trust-badge-premium">
                <FaStar />
                <span>Nota 5.0★</span>
              </div>
            </div>
          </div>
          
          <div className="hero-content-premium">
            <div className="doctor-presentation-premium" data-aos="fade-right" data-aos-delay="200">
              <div className="photo-frame-premium">
                <img src={doutoraFoto} alt="Dra. Brunna Lopes" className="doctor-photo-premium" />
                <div className="glow-effect-premium"></div>
                <div className="online-badge-premium">
                  <div className="online-dot-premium"></div>
                  <span>ONLINE AGORA</span>
                </div>
              </div>
              <div className="doctor-badge-premium">
                <h4>Dra. Brunna Lopes </h4>
                <p>CRM-MG 102898</p>
                <div className="doctor-specialty-premium">
                  <FaHeart />
                  <span>Endocrinologia e Metabologia</span>
                </div>
              </div>
            </div>
            
            <div className="hero-text-premium" data-aos="fade-left" data-aos-delay="200">
              <div className="urgency-alert-premium">
                <FaHeartbeat className="pulse-icon-premium" />
                <span>ATENDIMENTO ENDOCRINOLÓGICO ESPECIALIZADO</span>
              </div>

              <h1 className="hero-title-premium">
                <span className="title-main-premium">CONSULTA COM ENDOCRINOLOGISTA</span>
                <span className="title-sub-premium">Especialista em Hormônios e Metabolismo</span>
                <span className="price-highlight-premium">
                  <span className="price-label-premium">Por apenas</span>
                  <span className="price-value-premium">R$ 99,99</span>
                </span>
              </h1>

              <div className="benefits-preview-premium">
                <div className="benefit-item-premium">
                  <FaCheck />
                  <span>Consulta <strong>ESPECIALIZADA</strong></span>
                </div>
                <div className="benefit-item-premium">
                  <FaCheck />
                  <span><strong>Avaliação hormonal completa</strong></span>
                </div>
                <div className="benefit-item-premium">
                  <FaCheck />
                  <span>Plano de tratamento <strong>personalizado</strong></span>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="appointment-form-premium">
                <div className="form-header-premium">
                  <FaCalendarCheck className="form-icon-premium" />
                  <h3>Agendar Consulta com Endocrinologista</h3>
                  <p>Preencha os dados para <strong>atendimento especializado</strong></p>
                </div>

                <div className="form-group-premium">
                  <input 
                    type="text" 
                    name="name" 
                    placeholder="Nome completo *" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group-premium">
                  <input 
                    type="text" 
                    name="birthdate" 
                    placeholder="Data de nascimento (dd/mm/aaaa) *" 
                    value={formData.birthdate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group-premium">
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="E-mail *" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <button type="submit" className="cta-button-premium pulse-strong-premium">
                  <div className="button-content-premium">
                    <FaWhatsapp className="button-icon-premium" />
                    <span className="button-text-premium">CONSULTAR ENDOCRINOLOGISTA AGORA</span>
                    <FaArrowRight className="button-arrow-premium" />
                  </div>
                  <div className="button-subtext-premium">Atendimento Especializado • R$ 99,99</div>
                </button>

                <div className="form-security-premium">
                  <FaUserShield />
                  <span>Seus dados estão 100% protegidos</span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </header>

      {/* Services Section Premium */}
      <section className="services-section-premium">
        <div className="container">
          <div className="section-header-premium" data-aos="fade-up">
            <div className="section-badge-premium">
              <FaAward />
              <span>ATENDIMENTO ENDOCRINOLÓGICO ESPECIALIZADO</span>
            </div>
            <h2>Consulta com Endocrinologista Online</h2>
            <p>Atendimento especializado em <strong>hormônios, metabolismo e doenças endócrinas</strong></p>
          </div>
          
          <div className="service-card-single-premium" data-aos="fade-up" data-aos-delay="200">
            <div className="service-badge-featured-premium">
              <FaRocket />
              <span>ATENDIMENTO ESPECIALIZADO</span>
            </div>
            
            <div className="service-content-premium">
              <div className="service-icon-premium">
                <FaHeart />
              </div>
              
              <div className="service-info-premium">
                <h3>Consulta com Endocrinologista - Especialista</h3>
                <p className="service-description-premium">
                  Atendimento completo com endocrinologista especialista via videoconferência. 
                  <strong> Inclui avaliação hormonal completa</strong> e plano de tratamento personalizado.
                </p>
                
                <div className="service-features-premium">
                  <div className="feature-premium">
                    <FaVideo />
                    <span>Consulta por videoconferência</span>
                  </div>
                  <div className="feature-premium">
                    <FaWeight />
                    <span><strong>Avaliação metabólica completa</strong></span>
                  </div>
                  <div className="feature-premium">
                    <FaSyringe />
                    <span>Controle de diabetes e tireoide</span>
                  </div>
                  <div className="feature-premium">
                    <FaAppleAlt />
                    <span>Orientação nutricional hormonal</span>
                  </div>
                </div>
              </div>
              
              <div className="service-cta-premium">
                <div className="service-price-premium">
                  <span className="price-currency-premium">R$</span>
                  <span className="price-amount-premium">99,99</span>
                  <span className="price-period-premium">consulta especializada</span>
                </div>
                
                <button 
                  className="service-button-premium"
                  onClick={() => trackConversion("https://wa.me/5538999267532", "CONSULTA ENDOCRINOLOGISTA PREMIUM")}
                >
                  <FaWhatsapp />
                  <span>AGENDAR CONSULTA AGORA</span>
                  <FaArrowRight />
                </button>
                
                <div className="service-guarantee-premium">
                  <FaShieldAlt />
                  <span>Avaliação hormonal completa inclusa</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section Premium */}
      <section className="about-section-premium">
        <div className="container">
          <div className="about-content-premium">
            <div className="about-text-premium" data-aos="fade-right">
              <div className="about-badge-premium">
                <FaAward />
                <span>ESPECIALIDADE EM ENDOCRINOLOGIA</span>
              </div>
              
              <h2>Atendimento Endocrinológico Online por <span className="highlight-premium">R$99,99</span></h2>
              
              <div className="about-highlights-premium">
                <p className="highlight-text-premium">
                  ✅ <strong>Avaliação hormonal completa</strong><br/>
                  ✅ Controle de diabetes e tireoide<br/>
                  ✅ Tratamento para obesidade e metabolismo
                </p>
              </div>
              
              <p className="about-description-premium">
                Nossa equipe especializada em endocrinologia oferece atendimento humanizado e profissional através da telemedicina. 
                <strong>Todos os nossos endocrinologistas possuem CRM ativo</strong> e especialização em hormônios e metabolismo.
              </p>
              
              <button 
                className="cta-button-secondary-premium"
                onClick={() => trackConversion("https://wa.me/5538999267532", "SOBRE A ENDOCRINOLOGISTA")}
              >
                <FaWhatsapp /> Conheça nossa especialista
              </button>
            </div>
            
            <div className="benefits-grid-premium" ref={featuresRef} data-aos="fade-left">
              <div className="benefit-premium">
                <div className="benefit-icon-premium"><FaHeart /></div>
                <h3>Endocrinologistas Especialistas</h3>
                <p>Profissionais qualificados com CRM ativo e especialização em endocrinologia</p>
              </div>
              
              <div className="benefit-premium highlighted-premium">
                <div className="benefit-icon-premium"><FaFlask /></div>
                <h3>Avaliação Hormonal Completa</h3>
                <p><strong>Análise completa do seu perfil hormonal</strong> e metabólico</p>
              </div>
              
              <div className="benefit-premium">
                <div className="benefit-icon-premium"><FaSyringe /></div>
                <h3>Controle de Diabetes</h3>
                <p>Acompanhamento especializado para diabetes tipo 1 e 2</p>
              </div>
              
              <div className="benefit-premium">
                <div className="benefit-icon-premium"><FaWeight /></div>
                <h3>Tratamento para Obesidade</h3>
                <p>Abordagem completa para controle de peso e metabolismo</p>
              </div>
              
              <div className="benefit-premium">
                <div className="benefit-icon-premium"><FaAppleAlt /></div>
                <h3>Orientação Nutricional</h3>
                <p>Recomendações alimentares específicas para seu perfil hormonal</p>
              </div>
              
              <div className="benefit-premium">
                <div className="benefit-icon-premium"><FaFileAlt /></div>
                <h3>Laudos de Exames</h3>
                <p>Análise e interpretação de exames hormonais e metabólicos</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section Premium */}
      <section className="cta-section-premium" ref={ctaRef}>
        <div className="cta-effects-premium">
          <div className="light-effect-cta-premium"></div>
          <div className="medical-pattern-premium"></div>
        </div>

        <div className="container">
          <div className="cta-content-premium">
            <div className="cta-text-premium" data-aos="fade-right">
              <div className="cta-badge-premium">
                <FaRocket />
                <span>CUIDE DA SUA SAÚDE HORMONAL</span>
              </div>
              
              <h2>Atendimento Endocrinológico 24 Horas com Abrangência Nacional</h2>
              
              <div className="cta-features-premium">
                <div className="cta-feature-premium">
                  <FaCheck />
                  <span><strong>Avaliação hormonal completa</strong> inclusa</span>
                </div>
                <div className="cta-feature-premium">
                  <FaCheck />
                  <span>Consulta com <strong>endocrinologista especialista</strong></span>
                </div>
                <div className="cta-feature-premium">
                  <FaCheck />
                  <span>Controle de diabetes, tireoide e metabolismo</span>
                </div>
                <div className="cta-feature-premium">
                  <FaCheck />
                  <span><strong>Plano de tratamento personalizado</strong></span>
                </div>
              </div>
              
              <button 
                className="cta-button-final-premium pulse-strong-premium"
                onClick={() => trackConversion("https://wa.me/5538999267532", "CTA FINAL")}
              >
                <FaWhatsapp className="button-icon-large-premium" />
                <div className="button-text-container-premium">
                  <span className="button-main-text-premium">CONSULTAR ENDOCRINOLOGISTA AGORA</span>
                  <span className="button-sub-text-premium">R$ 99,99 • Avaliação Hormonal Inclusa</span>
                </div>
                <FaArrowRight className="button-arrow-premium" />
              </button>
            </div>
            
            <div className="cta-image-premium" data-aos="fade-left">
              <div className="doctor-card-cta-premium">
                <div className="photo-frame-cta-premium">
                  <img src={doutoraFoto} alt="Dra. Brunna Lopes atendendo" className="doctor-photo-cta-premium" />
                  <div className="glow-effect-cta-premium"></div>
                </div>
                <div className="doctor-quote-premium">
                  <div className="quote-stars-premium">
                    {[1,2,3,4,5].map(i => <FaStar key={i} />)}
                  </div>
                  <p>"Sua saúde hormonal é fundamental para o bem-estar. Estamos aqui para oferecer atendimento especializado 24 horas."</p>
                  <div className="quote-author-premium">
                    <span>- Dra. Brunna Lopes, CRM-MG 102898</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Premium */}
      <footer className="footer-premium">
        <div className="container">
          <div className="footer-content-premium">
            <div className="footer-logo-premium">
              <img src={logo} alt="Endocrinologista Agora" className="logo-footer-premium" />
            </div>
            <h3 className="footer-title-premium">Endocrinologista Agora</h3>
            <p className="footer-description-premium">
              Telemedicina especializada em endocrinologia com <strong>avaliação hormonal completa</strong>
            </p>
            
            <div className="footer-badges-premium">
              <div className="footer-badge-premium">
                <FaCertificate />
                <span>CFM Regulamentado</span>
              </div>
              <div className="footer-badge-premium">
                <FaShieldAlt />
                <span>Dados Seguros</span>
              </div>
              <div className="footer-badge-premium">
                <FaStar />
                <span>5.0★ Avaliação</span>
              </div>
            </div>
            
            <div className="footer-cta-premium">
              <button 
                className="whatsapp-button-premium pulse-premium"
                onClick={() => trackConversion("https://wa.me/5538999267532", "FOOTER WHATSAPP")}
              >
                <FaWhatsapp /> 
                <div className="whatsapp-button-text-premium">
                  <span>Fale conosco no WhatsApp</span>
                  <small>Atendimento especializado disponível</small>
                </div>
              </button>
            </div>
            
            <div className="copyright-premium">
              © {new Date().getFullYear()} Endocrinologista Agora. Todos os direitos reservados.
              <br/>
              <small>Telemedicina regulamentada pelo Conselho Federal de Medicina</small>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Componente App principal com rotas
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainContent />} />
        <Route path="/consultas" element={<MainContent />} />
        <Route path="/endocrinologia" element={<MainContent />} />
        <Route path="/endocrinologistas" element={<MainContent />} />
        <Route path="/telemedicina" element={<MainContent />} />
      </Routes>
    </Router>
  );
}

export default App;